import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDateTime, openAuthorizedDocument } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Download, Eye } from "lucide-react";
import { toast } from "sonner";
import DocumentPreviewModal from "@/components/DocumentPreviewModal";

function prettyFilename(name) {
  if (!name) return "";
  let base = name.split(/[\\/]/).pop();
  base = base.replace(/^\d{12,17}_/, "");
  base = base.replace(/[_-]watermarked(?=\.[^.]+$)/i, "");
  return base;
}

const FOLDERS = ["All","Financials","Tax Returns","Bank Statements","Corporate Documents","Collateral","Real Estate","Legal","Insurance","Other"];

export default function DocumentsPage() {
  const [allDocs, setAllDocs] = useState([]);
  const [q, setQ] = useState("");
  const [folder, setFolder] = useState("All");
  const [previewDoc, setPreviewDoc] = useState(null);

  useEffect(() => {
    api.get("/applications").then(async (r) => {
      const results = await Promise.all(r.data.map((a) => api.get(`/applications/${a.id}/documents`).then((d) => d.data.map((x) => ({ ...x, app: a }))).catch(() => [])));
      setAllDocs(results.flat());
    });
  }, []);

  const filtered = allDocs.filter((d) => {
    if (folder !== "All" && d.folder !== folder) return false;
    if (q && !d.filename.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="max-w-[1360px] space-y-14" data-testid="documents-page">
      <header>
        <div className="small-caps text-slate-500">Documents</div>
        <h1 className="mt-6 font-semibold text-[72px] leading-[0.9] tracking-tight text-[#1F2937]">
          The <span className="italic">vault.</span>
        </h1>
      </header>

      <div className="flex items-center gap-10 border-b border-slate-200 pb-4">
        <div className="flex-1 max-w-md">
          <div className="small-caps text-slate-500 mb-1">Search</div>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filename…" data-testid="docs-search-input"
            className="border-0 shadow-none rounded-none px-0 h-9 bg-transparent font-semibold text-[16px] focus-visible:ring-0 focus-visible:ring-offset-0" />
        </div>
        <div className="w-64">
          <div className="small-caps text-slate-500 mb-1">Folder</div>
          <Select value={folder} onValueChange={setFolder}>
            <SelectTrigger data-testid="docs-folder-filter" className="border-0 shadow-none rounded-none px-0 h-9 bg-transparent font-semibold text-[16px] focus:ring-0"><SelectValue /></SelectTrigger>
            <SelectContent>{FOLDERS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <div className="grid grid-cols-12 py-3 border-b border-slate-200 small-caps text-slate-500">
          <div className="col-span-1">No.</div>
          <div className="col-span-4">File</div>
          <div className="col-span-3">Application</div>
          <div className="col-span-2">Folder</div>
          <div className="col-span-2 text-right">Uploaded</div>
        </div>
        {filtered.map((d, i) => (
          <div key={d.id} className="grid grid-cols-12 items-baseline py-4 border-b border-slate-200" data-testid={`doc-item-${d.id}`}>
            <div className="col-span-1 font-mono text-[11px] text-slate-400">{String(i + 1).padStart(2, "0")}</div>
            <div className="col-span-4 flex items-center gap-3 min-w-0 pr-4">
              <FileText className="h-4 w-4 text-slate-400 shrink-0" />
              <button
                onClick={() => setPreviewDoc(d)}
                className="block w-full truncate font-semibold text-[15px] text-[#1F2937] text-left hover:underline underline-offset-4"
                data-testid={`docpage-open-${d.id}`}
                title={d.filename}
              >
                {prettyFilename(d.filename)}
              </button>
            </div>
            <div className="col-span-3">
              <Link to={`/app/applications/${d.app.id}`} className="small-caps text-slate-600 hover:text-[#1F2937]">{d.app.loan_number} →</Link>
            </div>
            <div className="col-span-2 small-caps text-slate-500">{d.folder}</div>
            <div className="col-span-2 flex items-center justify-end gap-4">
              <span className="small-caps text-slate-400">{fmtDateTime(d.uploaded_at)}</span>
              <button
                onClick={() => setPreviewDoc(d)}
                className="text-slate-400 hover:text-[#1F2937]"
                data-testid={`docpage-preview-${d.id}`}
                title="Preview inline"
              >
                <Eye className="h-4 w-4" />
              </button>
              <button
                onClick={() => openAuthorizedDocument(`/documents/${d.id}/download`, d.filename).catch((e) => toast.error(e.message || "Could not open document"))}
                className="text-slate-400 hover:text-[#1F2937]"
                data-testid={`docpage-download-${d.id}`}
                title="Download"
              >
                <Download className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="py-24 text-center font-semibold text-slate-400">Nothing on deposit.</div>}
      </div>

      <DocumentPreviewModal
        open={!!previewDoc}
        onClose={() => setPreviewDoc(null)}
        path={previewDoc ? `/documents/${previewDoc.id}/download` : null}
        filename={previewDoc?.filename}
        mimeType={previewDoc?.mime_type}
      />
    </div>
  );
}
